/**
 * AWS Amplify configuration and Axios interceptors.
 *
 * Amplify is configured from VITE_* environment variables so that the same
 * build artefact can be deployed to different environments by changing the
 * runtime config (injected at deploy time via CloudFront / S3 config object
 * or environment-specific .env files during local development).
 *
 * Required environment variables:
 *   VITE_COGNITO_USER_POOL_ID      — Cognito User Pool ID (e.g. us-east-1_XXXXXXXXX)
 *   VITE_COGNITO_CLIENT_ID         — Cognito App Client ID
 *   VITE_COGNITO_DOMAIN            — Cognito Hosted UI domain (e.g. auth.example.com)
 *   VITE_COGNITO_REDIRECT_SIGN_IN  — OAuth redirect sign-in URL
 *   VITE_COGNITO_REDIRECT_SIGN_OUT — OAuth redirect sign-out URL
 *   VITE_API_URL                   — Base URL for the API Gateway (e.g. https://api.example.com)
 */

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from '@aws-amplify/auth';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// ---------------------------------------------------------------------------
// Amplify configuration
// ---------------------------------------------------------------------------

// Amplify's `redirectSignIn` / `redirectSignOut` accept arrays of URLs. When
// multiple URLs are configured, Amplify automatically selects the one whose
// origin matches `window.location.origin` at the time the OAuth flow starts.
// We therefore offer every known origin so the flow completes from whichever
// domain the user started on — the new domain (mediamanager.greatrun.org), the
// old CloudFront domain (dhadios8999ew.cloudfront.net), and localhost for dev.
// The env-var values are kept first so config stays overridable per-environment,
// then merged with the known origins, de-duplicated, and empties filtered out.
// NOTE: every URL here MUST also be registered in the Cognito app client's
// callback/logout URL lists or Cognito rejects it with a redirect_mismatch.
// localhost:5173 is intentionally omitted because it is NOT registered in the
// Cognito app client (only localhost:3000 is).
const redirectSignIn = Array.from(new Set([
  import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN as string,
  'https://mediamanager.greatrun.org/callback',
  'https://dhadios8999ew.cloudfront.net/callback',
  'http://localhost:3000/callback',
].filter(Boolean)));

const redirectSignOut = Array.from(new Set([
  import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT as string,
  'https://mediamanager.greatrun.org',
  'https://dhadios8999ew.cloudfront.net',
  'http://localhost:3000',
].filter(Boolean)));

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn,
          redirectSignOut,
          responseType: 'code',
        },
      },
    },
  },
}, { ssr: false });

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — inject JWT into every request
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // First try reading token from localStorage (our manual OAuth flow)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
      const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
      const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      if (lastUser) {
        const idToken = localStorage.getItem(`${keyPrefix}.${lastUser}.idToken`);
        if (idToken) {
          config.headers.set('Authorization', idToken);
          return config;
        }
      }

      // Fallback: Amplify session (for email/password sign-in)
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        config.headers.set('Authorization', idToken);
      }
    } catch {
      // Not authenticated — let the request proceed without a token
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 429 rate-limit responses
// ---------------------------------------------------------------------------

/**
 * Maximum number of automatic retries on a 429 response.
 * We retry once after the Retry-After delay to give the user a seamless
 * experience for brief rate-limit windows (Requirement 14.3, 14.5).
 */
const MAX_RATE_LIMIT_RETRIES = 1;

// Extend the Axios config type to track retry attempts
interface RetryConfig extends InternalAxiosRequestConfig {
  _rateLimitRetries?: number;
  /** Set once we've attempted a token refresh for a 401 on this request. */
  _authRefreshAttempted?: boolean;
}

/**
 * Clear locally-cached Cognito tokens (our manual OAuth flow) so ProtectedRoute
 * sees an unauthenticated session and the app shows the sign-in screen instead
 * of leaving stale tokens that keep failing.
 */
function clearLocalAuthTokens(): void {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    if (lastUser) {
      localStorage.removeItem(`${keyPrefix}.${lastUser}.idToken`);
      localStorage.removeItem(`${keyPrefix}.${lastUser}.accessToken`);
      localStorage.removeItem(`${keyPrefix}.${lastUser}.refreshToken`);
      localStorage.removeItem(`${keyPrefix}.LastAuthUser`);
    }
  } catch {
    // ignore — best-effort cleanup
  }
}

/**
 * Refreshes the session using the refresh token stored by the manual OAuth flow
 * (see CallbackPage). The app exchanges the authorization code itself and keeps
 * the tokens in localStorage because Amplify's fetchAuthSession hangs behind
 * CloudFront in Amplify v6 — so Amplify never holds the session and cannot
 * refresh it. We therefore call Cognito's /oauth2/token endpoint directly with
 * grant_type=refresh_token, persist the new id/access tokens, and return the new
 * ID token. Returns null when there is no stored refresh token or the refresh is
 * rejected (e.g. the 30-day refresh window has lapsed).
 */
async function refreshTokensManually(): Promise<string | null> {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
    const domain = import.meta.env.VITE_COGNITO_DOMAIN as string;
    if (!clientId || !domain) {
      return null;
    }
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    if (!lastUser) {
      return null;
    }
    const refreshToken = localStorage.getItem(`${keyPrefix}.${lastUser}.refreshToken`);
    if (!refreshToken) {
      return null;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken,
    });
    const response = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!response.ok) {
      return null;
    }
    const tokens = (await response.json()) as {
      id_token?: string;
      access_token?: string;
      // A refresh-token grant does NOT return a new refresh_token; the existing
      // one remains valid until the end of its 30-day window.
      refresh_token?: string;
    };
    if (!tokens.id_token) {
      return null;
    }
    localStorage.setItem(`${keyPrefix}.${lastUser}.idToken`, tokens.id_token);
    if (tokens.access_token) {
      localStorage.setItem(`${keyPrefix}.${lastUser}.accessToken`, tokens.access_token);
    }
    if (tokens.refresh_token) {
      localStorage.setItem(`${keyPrefix}.${lastUser}.refreshToken`, tokens.refresh_token);
    }
    return tokens.id_token;
  } catch {
    return null;
  }
}

/**
 * When a session genuinely lapses (refresh failed), send the user to sign in
 * again rather than leaving them staring at a "network error". We preserve the
 * current path so they return where they were after re-auth. A hard redirect is
 * used so the whole app (and Redux) reinitialises cleanly against the login flow.
 * Guarded so we only ever redirect once and never from the login/callback pages
 * (which would create a redirect loop).
 */
let redirectingToLogin = false;
/** Test-only: reset the one-shot redirect guard between test cases. */
export function __resetSessionExpiredGuard(): void {
  redirectingToLogin = false;
}
export function handleSessionExpired(): void {
  clearLocalAuthTokens();
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (redirectingToLogin || path.startsWith('/login') || path.startsWith('/callback')) {
    return;
  }
  redirectingToLogin = true;
  const from = encodeURIComponent(path + window.location.search);
  window.location.assign(`/login?from=${from}`);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    // Handle 401 Unauthorized — token might have expired, try refreshing ONCE.
    if (error.response?.status === 401 && config) {
      if (!config._authRefreshAttempted) {
        config._authRefreshAttempted = true;
        try {
          // Primary path: refresh via the refresh token stored by the manual
          // OAuth flow (CallbackPage). Amplify never holds this session because
          // fetchAuthSession hangs behind CloudFront in Amplify v6, so a manual
          // /oauth2/token refresh is what actually keeps users signed in for the
          // full 30-day refresh window. refreshTokensManually persists the new
          // tokens to localStorage and returns the new ID token.
          let newToken = await refreshTokensManually();

          // Fallback: if there is no stored refresh token (e.g. an older
          // email/password Amplify session), try Amplify's own refresh.
          if (!newToken) {
            const session = await fetchAuthSession({ forceRefresh: true });
            newToken = session.tokens?.idToken?.toString() ?? null;
            if (newToken) {
              // Mirror the manual flow's localStorage write so subsequent
              // requests pick up the refreshed token.
              const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
              const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
              const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
              if (lastUser) {
                localStorage.setItem(`${keyPrefix}.${lastUser}.idToken`, newToken);
              }
            }
          }

          if (!newToken) {
            throw new Error('No token after refresh');
          }
          if (config.headers) {
            config.headers.set('Authorization', newToken);
          }
          return apiClient(config);
        } catch {
          // Refresh failed — the session has genuinely lapsed. Send to sign-in
          // cleanly instead of surfacing a raw network/auth error.
          handleSessionExpired();
        }
      }
      // Already attempted a refresh (or it failed) — surface as a clean auth error.
      return Promise.reject(error);
    }

    if (error.response?.status === 429 && config) {
      const retries = config._rateLimitRetries ?? 0;

      if (retries < MAX_RATE_LIMIT_RETRIES) {
        // Read the Retry-After header (seconds) and wait before retrying
        const retryAfterHeader = error.response.headers['retry-after'];
        const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 1;
        const delayMs = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : 1000;

        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));

        config._rateLimitRetries = retries + 1;
        return apiClient(config);
      }

      // Exhausted retries — surface the 429 to the caller so the UI can
      // display the rate-limit banner (Requirement 14.5).
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Auth helper utilities
// ---------------------------------------------------------------------------

/**
 * Returns the current user's ID token string, or null if not authenticated.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns the current Amplify auth session, or null if not authenticated.
 */
export async function getCurrentSession() {
  try {
    return await fetchAuthSession();
  } catch {
    return null;
  }
}
